// API のエラーレスポンスを FormErrorList が描画できる形に変換する。
//
// 戻り値は {フィールド名: [メッセージ, ...]} で、値は必ず文字列の配列になる。
// フィールドを特定できないものは form にまとめる。
//
// バックエンドは以下の2形式を返す。
//   新: {errors: [{msg, loc}, ...]}  matomete-solar-api-rest
//   旧: {error: 文字列 | {フィールド: [メッセージ]} | {Code, Message}}
// 移行が完了していない API があるため両方を受け付ける。

const DEFAULT_MESSAGE = 'エラーが発生しました。システム担当にお問い合わせください。'

const FORM_KEY = 'form'

// Cognito の例外名 -> 利用者向けの文言。
// 新形式では boto の ClientError を文字列化したものがそのまま msg に入ってくるため、
// 例外名が含まれていれば置き換える。
const COGNITO_MESSAGES = {
  CodeMismatchException: '不正な認証コードです。メールをご確認ください。',
  ExpiredCodeException: '認証コードが期限切れです。システム担当にお問い合わせください。',
  InvalidPasswordException: 'パスワードは英大文字、小文字、数字の全てを含む8文字以上が必要です。',
  LimitExceededException: '試行回数が上限に達しました。しばらく経ってからお試しください。',
  NotAuthorizedException: '権限がありません。',
  UserNotFoundException: 'ユーザ名が存在しません。',
  UsernameExistsException: '既にそのメールアドレスのユーザが存在します。',
}

// FormErrorList に渡す前の最後の砦。文字列以外は描画すると React が例外を投げるので落とす。
export const toMessageList = errors => {
  if(typeof errors == 'string') {
    return [errors]
  }

  if(!Array.isArray(errors)) {
    return []
  }

  return errors.filter(item => typeof item == 'string')
}

export const parseApiFormErrors = err => {
  const data = err && err.response ? err.response.data : null

  if(data && typeof data == 'object') {
    if(Array.isArray(data.errors)) {
      const formErrors = fromErrorList(data.errors)
      if(Object.keys(formErrors).length > 0) {
        return formErrors
      }
    }

    if(data.error != null) {
      const formErrors = fromLegacyError(data.error)
      if(Object.keys(formErrors).length > 0) {
        return formErrors
      }
    }

    return {[FORM_KEY]: [DEFAULT_MESSAGE]}
  }

  return {[FORM_KEY]: [toMessage(err)]}
}

// 新形式 {errors: [{msg, loc}, ...]}
const fromErrorList = errors => {
  const formErrors = {}

  for(const item of errors) {
    if(!item || typeof item != 'object') {
      continue
    }

    const message = translate(item.msg || item.message)
    if(!message) {
      continue
    }

    push(formErrors, toFieldName(item), message)
  }

  return formErrors
}

// 旧形式 {error: ...}
const fromLegacyError = error => {
  if(typeof error == 'string') {
    return {[FORM_KEY]: [translate(error)]}
  }

  if(typeof error != 'object') {
    return {}
  }

  // Cognito の例外をそのまま返していたもの
  if(error.Code) {
    const message = COGNITO_MESSAGES[error.Code]
      || `${DEFAULT_MESSAGE} Error: ${asText(error.Message)}`

    return {[FORM_KEY]: [message]}
  }

  const formErrors = {}

  for(const [field, messages] of Object.entries(error)) {
    for(const message of toMessageList(messages)) {
      push(formErrors, field, message)
    }
  }

  return formErrors
}

// pydantic の loc は ['phone_number'] のような配列。旧 ErrorList は key を使う。
const toFieldName = item => {
  const loc = item.loc

  if(Array.isArray(loc) && typeof loc[0] == 'string') {
    return loc[0]
  }

  if(typeof loc == 'string') {
    return loc
  }

  if(typeof item.key == 'string') {
    return item.key
  }

  return FORM_KEY
}

const translate = message => {
  const text = asText(message)
  if(!text) {
    return ''
  }

  for(const [code, translated] of Object.entries(COGNITO_MESSAGES)) {
    if(text.indexOf(code) >= 0) {
      return translated
    }
  }

  return text
}

// レスポンスを伴わないエラー (通信断など)
const toMessage = err => {
  if(typeof err == 'string') {
    return err
  }

  const text = err && asText(err.message)

  return text ? `${DEFAULT_MESSAGE} Error: ${text}` : DEFAULT_MESSAGE
}

const asText = value => (typeof value == 'string' ? value : '')

const push = (formErrors, field, message) => {
  if(!formErrors[field]) {
    formErrors[field] = []
  }

  formErrors[field].push(message)
}
